import { useTranslation } from 'react-i18next'
import { Modal, Row, Col } from 'react-bootstrap'
import { isValid } from 'date-fns'

import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { prefix } from '../common'
import { useField } from '../../../context'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'

const getValue = (value: string) => (+value < 0 ? '0' : value)

export const OptionsForm = ({ show, onHide, onSave }) => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({ prefix })

  const courseSynchronousProperties = useField('courseSynchronousProperties')
  const courseSynchronous = useField('courseSynchronous')
  const hoursField = useField('hours')
  const canRestart = useField('canRestart')
  const isSynchronous = courseSynchronous.value

  const { value } = courseSynchronousProperties
  const { min, max } = value

  const setMin = (value: string) => {
    courseSynchronousProperties.change({
      min: value,
      max,
    })
  }

  const setMax = (value: string) => {
    courseSynchronousProperties.change({
      min,
      max: value,
    })
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="mt-2 border-bottom-0">
        <Modal.Title>{tRelative('options')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          {isSynchronous && (
            <>
              <Col xs={12} md={6}>
                <TextInput
                  name="min"
                  type="number"
                  label={tRelative(`notes.min`)}
                  value={min}
                  onChange={({ target }) => {
                    const newMin = getValue(target.value)
                    setMin(newMin)
                    if (+newMin > +(max || 0) && newMin !== '') setMax(newMin)
                  }}
                />
              </Col>
              <Col xs={12} md={6}>
                <TextInput
                  name="max"
                  type="number"
                  label={tRelative(`notes.max`)}
                  value={max}
                  onChange={({ target }) => {
                    const newMax = getValue(target.value)
                    setMax(newMax)
                  }}
                />
              </Col>
            </>
          )}
          <Col xs={12}>
            <TextInput
              name="hours"
              type="number"
              label={
                <InputLabel
                  label={
                    <>
                      {tRelative(`hoursTotal.label`)}
                      <CommonToolTip
                        text={tRelative(`hoursTotal.tooltip`)}
                        position="right"
                        className={'tabsTooltip'}
                      >
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className={`ml-2`}
                        />
                      </CommonToolTip>
                    </>
                  }
                />
              }
              value={hoursField.value}
              onChange={({ target }) => {
                const newHours = getValue(target.value)
                hoursField.change(newHours)
              }}
            />
          </Col>
          <Col>
            <InputLabel
              label={
                <>
                  {tRelative(`canRestartInput.label`)}
                  <CommonToolTip
                    text={tRelative(`canRestartInput.tooltip`)}
                    position="right"
                    className={'tabsTooltip'}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className={`ml-2`} />
                  </CommonToolTip>
                </>
              }
            />
            {[
              { label: tRelative('canRestartInput.yes'), value: true },
              { label: tRelative('canRestartInput.no'), value: false },
            ].map(({ label, value }, index) => (
              <CheckBoxRadioButtonInput
                key={index}
                name={'canRestart-' + index}
                checked={canRestart.value === value}
                inline={true}
                type={'radio'}
                label={label}
                onChange={() => {
                  canRestart.change(value)
                }}
                error={''}
              />
            ))}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="ml-3 mr-3 mb-2 border-top-0">
        <Button
          size={ButtonSizes.Small}
          className="mr-2"
          variant={ButtonVariant.Secondary}
          onClick={onHide}
        >
          {t('commons.buttons.cancel')}
        </Button>
        <Button
          disabled={!isValid}
          size={ButtonSizes.Small}
          onClick={() => {
            onSave()
          }}
        >
          {t('commons.buttons.accept')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default OptionsForm
